import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { AuthGuard } from "@/core/auth/guards/AuthGuard";
import { GuestGuard } from "@/core/auth/guards/GuestGuard";
import { LoginGuard } from "@/core/auth/guards/LoginGuard";

// Auth feature pages
import { AuthEntryPage } from "@/features/auth/pages/AuthEntryPage";
import { AuthFlowLayout } from "@/features/auth/components/AuthFlowLayout";
import { RegistrationBackGuard } from "@/features/auth/components/RegistrationBackGuard";
import { AuthLoginPage } from "@/features/auth/pages/AuthLoginPage";
import { ForgotPasswordEmailPage } from "@/features/auth/pages/ForgotPasswordEmailPage";
import { ForgotPasswordCodePage } from "@/features/auth/pages/ForgotPasswordCodePage";
import { ForgotPasswordNewPasswordPage } from "@/features/auth/pages/ForgotPasswordNewPasswordPage";
import { ForgotPasswordSuccessPage } from "@/features/auth/pages/ForgotPasswordSuccessPage";

// Register sub-flow (M1 — existing pages)
import { AuthBiometricIntroPage } from "@/features/auth/pages/AuthBiometricIntroPage";
import { AuthBiometricVerificationPage } from "@/features/auth/pages/AuthBiometricVerificationPage";
import { AuthCountrySelectPage } from "@/features/auth/pages/AuthCountrySelectPage";
import { AuthIdentityVerificationPage } from "@/features/auth/pages/AuthIdentityVerificationPage";
import { AuthOtpCodePage } from "@/features/auth/pages/AuthOtpCodePage";
import { AuthOtpContactPage } from "@/features/auth/pages/AuthOtpContactPage";
import { AuthPasswordSetupPage } from "@/features/auth/pages/AuthPasswordSetupPage";
import { AuthRegisterConsentPage } from "@/features/auth/pages/AuthRegisterConsentPage";
import { AuthRegisterSuccessPage } from "@/features/auth/pages/AuthRegisterSuccessPage";

// Sprint 2 — layout autenticado
import { AppLayout } from "@/shared/components/layout/AppLayout";

// Dashboard
import { PerfilamientoEntryPage } from "@/features/perfilamiento/pages/PerfilamientoEntryPage";
import { PerfilamientoFilterPage } from "@/features/perfilamiento/pages/PerfilamientoFilterPage";
import { PerfilamientoQuestionnairePage } from "@/features/perfilamiento/pages/PerfilamientoQuestionnairePage";
import { PerfilamientoResultPage } from "@/features/perfilamiento/pages/PerfilamientoResultPage";
import { PerfilamientoAdvisoryPage } from "@/features/perfilamiento/pages/PerfilamientoAdvisoryPage";
import { DashboardPage } from "@/features/dashboard/pages/DashboardPage";
import { PerfilPlaceholderPage } from "@/features/dashboard/pages/PerfilPlaceholderPage";
import { SuspendAccountPage } from "@/features/profile/pages/SuspendAccountPage";

// Productos
import { ProductosPage } from "@/features/productos/pages/ProductosPage";

// Simulador
import { SimuladorPage } from "@/features/simulador/pages/SimuladorPage";
import { DescargaExitoPage } from "@/features/simulador/pages/DescargaExitoPage";

// Contacto
import { ContactoPage } from "@/features/contacto/pages/ContactoPage";

export function AppRouter() {
  return (
    <BrowserRouter>
      <RegistrationBackGuard />
      <Routes>
        {/* ----------------------------------------------------------------
            Public / landing
            ---------------------------------------------------------------- */}
        <Route
          path="/"
          element={
            <LoginGuard>
              <AuthFlowLayout>
                <AuthEntryPage />
              </AuthFlowLayout>
            </LoginGuard>
          }
        />

        {/* ----------------------------------------------------------------
            Guest-only routes (redirect to /dashboard when authenticated)
            ---------------------------------------------------------------- */}
        <Route
          path="/login"
          element={
            <LoginGuard>
              <AuthFlowLayout>
                <AuthLoginPage />
              </AuthFlowLayout>
            </LoginGuard>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <GuestGuard>
              <AuthFlowLayout>
                <ForgotPasswordEmailPage />
              </AuthFlowLayout>
            </GuestGuard>
          }
        />
        <Route
          path="/forgot-password/code"
          element={
            <GuestGuard>
              <AuthFlowLayout>
                <ForgotPasswordCodePage />
              </AuthFlowLayout>
            </GuestGuard>
          }
        />
        <Route
          path="/forgot-password/password"
          element={
            <GuestGuard>
              <AuthFlowLayout>
                <ForgotPasswordNewPasswordPage />
              </AuthFlowLayout>
            </GuestGuard>
          }
        />
        <Route
          path="/forgot-password/success"
          element={
            <GuestGuard>
              <ForgotPasswordSuccessPage />
            </GuestGuard>
          }
        />
        <Route
          path="/register"
          element={<Navigate replace to="/register/consent" />}
        />

        {/* Register sub-flow (M1 — wrapped in GuestGuard) */}
        <Route
          path="/register/consent"
          element={
            <GuestGuard>
              <AuthFlowLayout>
                <AuthRegisterConsentPage />
              </AuthFlowLayout>
            </GuestGuard>
          }
        />
        <Route
          path="/register/country"
          element={
            <GuestGuard>
              <AuthFlowLayout>
                <AuthCountrySelectPage />
              </AuthFlowLayout>
            </GuestGuard>
          }
        />
        <Route
          path="/register/identity"
          element={
            <GuestGuard>
              <AuthFlowLayout>
                <AuthIdentityVerificationPage />
              </AuthFlowLayout>
            </GuestGuard>
          }
        />
        <Route
          path="/register/biometric"
          element={
            <GuestGuard>
              <AuthFlowLayout mode="centered">
                <AuthBiometricIntroPage />
              </AuthFlowLayout>
            </GuestGuard>
          }
        />
        <Route
          path="/register/permissions"
          element={
            <GuestGuard>
              <AuthFlowLayout mode="centered">
                <AuthBiometricVerificationPage />
              </AuthFlowLayout>
            </GuestGuard>
          }
        />
        <Route
          path="/register/otp"
          element={
            <GuestGuard>
              <AuthFlowLayout>
                <AuthOtpContactPage />
              </AuthFlowLayout>
            </GuestGuard>
          }
        />
        <Route
          path="/register/otp/code"
          element={
            <GuestGuard>
              <AuthFlowLayout>
                <AuthOtpCodePage />
              </AuthFlowLayout>
            </GuestGuard>
          }
        />
        <Route
          path="/register/password"
          element={
            <GuestGuard>
              <AuthFlowLayout>
                <AuthPasswordSetupPage />
              </AuthFlowLayout>
            </GuestGuard>
          }
        />
        <Route
          path="/register/success"
          element={
            <GuestGuard>
              <AuthFlowLayout mode="centered">
                <AuthRegisterSuccessPage />
              </AuthFlowLayout>
            </GuestGuard>
          }
        />

        {/* ----------------------------------------------------------------
            Protected routes — layout autenticado (Sidebar + Header + Footer)
            ---------------------------------------------------------------- */}
        <Route
          path="/dashboard"
          element={
            <AuthGuard>
              <AppLayout>
                <DashboardPage />
              </AppLayout>
            </AuthGuard>
          }
        />
        <Route
          path="/perfilamiento"
          element={
            <AuthGuard>
              <AppLayout>
                <PerfilamientoEntryPage />
              </AppLayout>
            </AuthGuard>
          }
        />
        <Route
          path="/perfilamiento/filtro/:processId"
          element={
            <AuthGuard>
              <AppLayout>
                <PerfilamientoFilterPage />
              </AppLayout>
            </AuthGuard>
          }
        />
        <Route
          path="/perfilamiento/cuestionario/:processId"
          element={
            <AuthGuard>
              <AppLayout>
                <PerfilamientoQuestionnairePage />
              </AppLayout>
            </AuthGuard>
          }
        />
        <Route
          path="/perfilamiento/resultado/:processId"
          element={
            <AuthGuard>
              <AppLayout>
                <PerfilamientoResultPage />
              </AppLayout>
            </AuthGuard>
          }
        />
        <Route
          path="/productos"
          element={
            <AuthGuard>
              <AppLayout>
                <ProductosPage />
              </AppLayout>
            </AuthGuard>
          }
        />
        <Route
          path="/simulador"
          element={
            <AuthGuard>
              <AppLayout>
                <SimuladorPage />
              </AppLayout>
            </AuthGuard>
          }
        />
        <Route
          path="/simulador/descarga-exito"
          element={
            <AuthGuard>
              <AppLayout>
                <DescargaExitoPage />
              </AppLayout>
            </AuthGuard>
          }
        />
        <Route
          path="/contacto"
          element={
            <AuthGuard>
              <AppLayout>
                <ContactoPage />
              </AppLayout>
            </AuthGuard>
          }
        />
        <Route
          path="/perfilamiento/asesoria/:processId"
          element={
            <AuthGuard>
              <AppLayout>
                <PerfilamientoAdvisoryPage />
              </AppLayout>
            </AuthGuard>
          }
        />
        <Route
          path="/perfil"
          element={
            <AuthGuard>
              <AppLayout>
                <PerfilPlaceholderPage />
              </AppLayout>
            </AuthGuard>
          }
        />
        <Route
          path="/suspender-cuenta"
          element={
            <AuthGuard>
              <AppLayout>
                <SuspendAccountPage />
              </AppLayout>
            </AuthGuard>
          }
        />

        {/* ----------------------------------------------------------------
            Catch-all fallback
            ---------------------------------------------------------------- */}
        <Route path="*" element={<Navigate replace to="/" />} />
      </Routes>
    </BrowserRouter>
  );
}
